import { ROLES } from ".";
import { ActionStatus } from "../../common/constants/action-status";
import { PersonaRole, PersonaSegment } from "../../common/constants/user-persona";
import { isGuestCheckoutPage, isSuccessPage } from "../../common/environment";
import { getGuestTypeFromOrderItem } from "../../common/utils/cart-util";
import { equalsIgnoreCase } from "../../common/utils/util";
import { waitForState } from "../../store/store";
import { StoreState } from "../../store/store.interface";
import { getCart } from "../cart/shop-cart/store/shop-cart.selectors";
import { getOrderApplicationOrderCompletion } from "../order-confirmation/store/order-completion.selectors";
import { UserProfile, UserStatus } from "./user-profile.interface";

/*----- CREATE SELECTORS ------*/

export const getUserProfileStatus = (state: StoreState) => state.userProfileState.userProfileResponse.status;

export const selectUser = (state: StoreState) => state.userProfileState.userProfileResponse.data;

export const selectSegment = (state: StoreState) => {
  const user = selectUser(state);
  return user?.persona && user.persona.segment;
};

export const isSegUser =  (user: UserProfile) => {
  return user?.persona && equalsIgnoreCase(user.persona.segment, PersonaSegment.SEG);
};

export const isHeStudent = (state: StoreState) => {
  const user = selectUser(state);
  return user?.persona 
    && equalsIgnoreCase(user.persona.segment, PersonaSegment.HE) 
    && equalsIgnoreCase(user.persona.role, PersonaRole.STUDENT);
}

export const isSegIndividualStudent = (state: StoreState) => {
  const user = selectUser(state);
  return user?.persona 
    && equalsIgnoreCase(user.persona.segment, PersonaSegment.SEG_INDIVIDUAL) 
    && equalsIgnoreCase(user.persona.role, PersonaRole.STUDENT);
}

export const isProfessionalStudent = (state: StoreState) => {
  const user = selectUser(state);
  return user?.persona 
    && equalsIgnoreCase(user.persona.segment, PersonaSegment.PROFESSIONAL) 
    && equalsIgnoreCase(user.persona.role, PersonaRole.STUDENT);
}

export const isAdminUser =  (state: StoreState) => {
  const user = selectUser(state);
  return user?.persona && equalsIgnoreCase(user.persona.role, PersonaRole.ADMIN);
};

//We are checking for data because on the user-profile.thunks we are returning null if the MH_TOKEN is not present
export const isSuccessUser = (state: StoreState) => !!state.userProfileState.userProfileResponse.data;

export const isFailureUser = (state: StoreState) => state.userProfileState.userProfileResponse.status === ActionStatus.failed;

export const isSuccessOrFailureUser = (state: StoreState) => [ActionStatus.failed, ActionStatus.succeeded].includes(state.userProfileState.userProfileResponse.status);

export const isLoggedOutUser = (state: StoreState) => { 
  return state.userProfileState.userProfileResponse.status === ActionStatus.failed || 
  (state.userProfileState.userProfileResponse.status === ActionStatus.succeeded && !state.userProfileState.userProfileResponse.data)
};

export const isUserHomeschooler = (state: StoreState) => {
  const userInfo = selectUser(state);
  return userInfo?.persona?.role?.toLowerCase() ===  PersonaRole.HOMESCHOOLER.toLocaleLowerCase();
};

export const isUserInstructor = (state: StoreState) => {
  const userInfo = selectUser(state);
  return userInfo?.userType == ROLES.INS;
};

export const isUserVerifiedInstructor = (state: StoreState) => {
  const userInfo = selectUser(state);
  return userInfo?.userType === ROLES.INS && userInfo?.status === UserStatus.active;
};

export const isUserLearner = (state: StoreState) => {
  return selectUser(state).userType === ROLES.STU;
}

export const getUserDataForUserAnalytics = (state: StoreState, showAffiliations = false, showEmail = false) => {
  if (isGuestCheckoutPage()) {
    return {
      loggedIn: false,
      guestType: getGuestType(state),
    };
  }

  const userInfo = selectUser(state);
  return getAnalyticsSafeData(userInfo, showAffiliations, showEmail);
};

function getGuestType(state: StoreState) {
  if (isSuccessPage(window.location.toString())) {
    return getOrderApplicationOrderCompletion(state);
  }
  return getGuestTypeFromOrderItem(getCart(state)?.products[0]!);
}

export const getAnalyticsSafeData = (userInfo: UserProfile, showAffiliations = false, showEmail = false) => {
  // CAREFUL! Do not include any PII or personal information here.
  return userInfo ? {
    userType: userInfo.userType,
    ...(userInfo.persona && { persona: userInfo.persona }),
    ...(showEmail && { email: userInfo?.email }),
    ...(showAffiliations && { affiliations: userInfo?.affiliations }),
    loggedIn: true
  } : {
    loggedIn: false
  };
}

export function getUserPageView(state: StoreState) {
  const userInfo = selectUser(state);
  if (userInfo && userInfo.userType === 'instructor' && userInfo.persona.segment === PersonaSegment.HE) {
    return 'Instructor View';
  } else if(userInfo && userInfo.userType === 'learner' && userInfo.persona.segment === PersonaSegment.HE) { 
    return 'Student View';
  } else {
    return 'Guest View';
  }
}

export const selectPassword = (state: StoreState) => state.userProfileState.updateProfile.password;

export const selectPasswordValidationStatus = (state: StoreState) => state.userProfileState.updateProfile.passCompState;

export const selectSecurityQuestion = (state: StoreState) => state.userProfileState.userProfileResponse?.data?.question;

export const selectSecurityQuestionList = (state: StoreState) => state.userProfileState.securityQuestionResponse.data || [];

export const selectUniqueEmailStatusIsLoading = (state: StoreState) => state.userProfileState.updateProfile.uniqueEmailState.status === ActionStatus.loading;

export const getSchoolResponse = (state: StoreState) => state.userProfileState.updateProfile.schoolResponse.data || [];

export const getSchoolStatus = (state: StoreState) => state.userProfileState.updateProfile.schoolResponse.status;

export const getSchoolByIdResponse = (state: StoreState) => state.userProfileState.updateProfile.schoolByIdResponse.data || [];

export const getSchoolByIdStatus = (state: StoreState) => state.userProfileState.updateProfile.schoolByIdResponse.status;

export const getDesciplineResponse = (state: StoreState) => state.userProfileState.updateProfile.desciplineResponse.data || [];

export const getDepartmentCache = (state: StoreState) =>
  state.userProfileState.updateProfile.departmentCache || {};

export const getDepartmentsByCountry = (countryCode: string) => (state: StoreState) =>
  state.userProfileState.updateProfile.departmentCache?.[countryCode]?.data || [];

export const getUpdateProfileAffiliations = (state: StoreState) => state.userProfileState.updateProfile.affiliations || [];

export const waitForUserSelf = async () => await waitForState(state => state.userProfileState.selfCall);